import { useLayoutEffect, useRef } from 'react';

/**
 * Observes height changes of the element associated with the returned ref.
 * Calls the onChangeHeight callback when a change occurs.
 */
export default function useHeightObserver({
  onChangeHeight,
}: {
  onChangeHeight: (height: number, target: HTMLElement) => void;
}) {
  const elementRef = useRef<HTMLElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const prevHeightRef = useRef<number>(0);

  useLayoutEffect(() => {
    if (!elementRef.current || observerRef.current) {
      return;
    }

    observerRef.current = new ResizeObserver(entries => {
      // Observer callback will always have at least one entry
      const [entry] = entries;
      let height = 0;

      if (entry.contentBoxSize.length > 0) {
        height = entry.contentBoxSize[0]?.blockSize;
      }
      if (height > 0 && height !== prevHeightRef.current) {
        prevHeightRef.current = height;
        if (elementRef.current) {
          onChangeHeight(height, elementRef.current);
        }
      }
    });
    observerRef.current.observe(elementRef.current);

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  return elementRef;
}
